import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as FiIcons from "react-icons/fi";
import { usePlaidLink } from "react-plaid-link";
import SafeIcon from "../../common/SafeIcon";
import { useClient } from "../../context/ClientContext";
import ClientBankAccountCard from "../../components/clients/banking/ClientBankAccountCard";
import { clientBankingService } from "../../services/client/clientBankingService";

const { FiAlertTriangle, FiCheckCircle, FiCreditCard, FiPlus, FiRefreshCw } = FiIcons;

function SummaryCard({ label, value, detail, icon, tone }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-black tracking-tight text-slate-900">{value}</p>
          <p className="mt-1 text-xs text-slate-400">{detail}</p>
        </div>
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
          <SafeIcon icon={icon} />
        </span>
      </div>
    </section>
  );
}

export default function ClientBanking() {
  const { activeWorkspace } = useClient();
  const organizationId = activeWorkspace?.id || null;
  const organizationName = activeWorkspace?.name || "Your business";
  const currency = activeWorkspace?.currency || "";

  const [accounts, setAccounts] = useState([]);
  const [loadedOrganizationId, setLoadedOrganizationId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const organizationRef = useRef(organizationId);
  organizationRef.current = organizationId;
  const linkOrganizationRef = useRef(null);
  const openedTokenRef = useRef(null);
  const [busyItemId, setBusyItemId] = useState(null);
  const [busyAccountId, setBusyAccountId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const visibleAccounts = loadedOrganizationId === organizationId ? accounts : [];

  const loadAccounts = useCallback(async (orgId, showLoading = false) => {
    if (!orgId) {
      setAccounts([]);
      setLoadedOrganizationId(null);
      setLoading(false);
      setError("Select an active client business to view banking.");
      return;
    }

    if (showLoading) setLoading(true);
    setError("");

    try {
      const result = await clientBankingService.getAccounts(orgId);
      if (orgId !== organizationId) return;
      setAccounts(result);
      setLoadedOrganizationId(orgId);
    } catch (loadError) {
      if (orgId === organizationId) {
        setAccounts([]);
        setLoadedOrganizationId(null);
        setError(loadError?.message || "Unable to load connected bank accounts.");
      }
    } finally {
      if (orgId === organizationId) setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    let current = true;
    setAccounts([]);
    setLoadedOrganizationId(null);
    setLoading(Boolean(organizationId));
    setError("");
    setNotice("");
    setLinkToken(null);
    linkOrganizationRef.current = null;
    openedTokenRef.current = null;
    setConnecting(false);
    setBusyItemId(null);
    setBusyAccountId(null);

    if (!organizationId) {
      setLoading(false);
      setError("Select an active client business to view banking.");
      return () => { current = false; };
    }

    clientBankingService.getAccounts(organizationId)
      .then((result) => {
        if (!current) return;
        setAccounts(result);
        setLoadedOrganizationId(organizationId);
      })
      .catch((loadError) => {
        if (!current) return;
        setError(loadError?.message || "Unable to load connected bank accounts.");
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => { current = false; };
  }, [organizationId]);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      const linkedOrganizationId = linkOrganizationRef.current;
      setLinkToken(null);
      openedTokenRef.current = null;
      if (!linkedOrganizationId || linkedOrganizationId !== organizationRef.current) {
        setConnecting(false);
        return;
      }
      setError("");
      setNotice("");

      try {
        const exchange = await clientBankingService.exchangePublicToken({
          publicToken,
          organizationId: linkedOrganizationId,
          metadata,
        });
        const itemId = exchange?.item?.item_id;
        if (!itemId) throw new Error("Plaid exchange did not return an Item ID.");

        // A successful exchange is saved even if account synchronization fails.
        try {
          await clientBankingService.syncAccounts({
            organizationId: linkedOrganizationId,
            itemId,
          });
        } catch (syncError) {
          if (linkedOrganizationId === organizationRef.current) {
            setError(`Bank connected, but account synchronization failed: ${syncError.message}`);
            await loadAccounts(linkedOrganizationId, true);
          }
          return;
        }

        if (linkedOrganizationId === organizationRef.current) {
          await loadAccounts(linkedOrganizationId, true);
          setNotice("Bank connected and accounts synchronized. Transactions were not posted.");
        }
      } catch (connectError) {
        if (linkedOrganizationId === organizationRef.current) {
          setError(connectError?.message || "Unable to complete the bank connection.");
        }
      } finally {
        if (linkedOrganizationId === organizationRef.current) setConnecting(false);
      }
    },
    onExit: (exitError) => {
      setLinkToken(null);
      openedTokenRef.current = null;
      setConnecting(false);
      if (linkOrganizationRef.current === organizationRef.current && exitError) {
        setError(exitError.display_message || exitError.error_message || "Plaid Link closed with an error.");
      }
    },
  });

  useEffect(() => {
    if (linkToken && ready && openedTokenRef.current !== linkToken &&
        linkOrganizationRef.current === organizationId) {
      openedTokenRef.current = linkToken;
      open();
    }
  }, [linkToken, ready, open, organizationId]);

  const connectBank = async () => {
    if (!organizationId || connecting) return;
    const requestedOrgId = organizationId;
    setConnecting(true);
    setError("");
    setNotice("");

    try {
      const result = await clientBankingService.createLinkToken(requestedOrgId);
      if (organizationRef.current !== requestedOrgId) return;
      linkOrganizationRef.current = requestedOrgId;
      openedTokenRef.current = null;
      setLinkToken(result.link_token);
    } catch (linkError) {
      if (organizationRef.current === requestedOrgId) {
        setError(linkError?.message || "Unable to start the Plaid connection.");
      }
      setConnecting(false);
    }
  };

  const refresh = () => {
    if (!organizationId || refreshing || loading) return;
    setRefreshing(true);
    clientBankingService.getAccounts(organizationId)
      .then((result) => {
        if (organizationId !== activeWorkspace?.id) return;
        setAccounts(result);
        setLoadedOrganizationId(organizationId);
      })
      .catch((refreshError) => {
        if (organizationId === activeWorkspace?.id) {
          setError(refreshError?.message || "Unable to refresh bank accounts.");
        }
      })
      .finally(() => setRefreshing(false));
  };

  const syncAccounts = async (account) => {
    if (!organizationId || !account.plaid_item_id || busyItemId) return;
    setBusyItemId(account.plaid_item_id);
    setError("");
    setNotice("");

    try {
      await clientBankingService.syncAccounts({
        organizationId,
        itemId: account.plaid_item_id,
      });
      await loadAccounts(organizationId);
      if (organizationRef.current === organizationId) setNotice("Account synchronization completed.");
    } catch (syncError) {
      if (organizationRef.current === organizationId) setError(syncError?.message || "Unable to synchronize bank accounts.");
    } finally {
      setBusyItemId(null);
    }
  };

  const syncTransactions = async (account) => {
    if (!organizationId || !account.account_id || busyAccountId) return;
    setBusyAccountId(account.id);
    setError("");
    setNotice("");

    try {
      const result = await clientBankingService.syncTransactions({
        organizationId,
        account,
      });
      const count = Number(result?.selectedStaged ?? result?.staged ?? 0);
      if (organizationRef.current === organizationId) setNotice(
        count
          ? `${count} transaction${count === 1 ? "" : "s"} staged for review. No transactions were posted to the ledger.`
          : "Transaction synchronization completed. No new transactions were staged."
      );
      await loadAccounts(organizationId);
    } catch (syncError) {
      if (organizationRef.current === organizationId) setError(syncError?.message || "Unable to synchronize transactions.");
    } finally {
      setBusyAccountId(null);
    }
  };

  const totals = useMemo(() => ({
    mapped: visibleAccounts.filter((account) => Boolean(account.account_id)).length,
    unmapped: visibleAccounts.filter((account) => !account.account_id).length,
  }), [visibleAccounts]);

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-violet-700">Financial management</p>
          <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-900">Banking</h1>
          <p className="mt-2 text-sm text-slate-600">
            View connected bank accounts and securely synchronize activity for {organizationName}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={!organizationId || loading || refreshing}
            className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <SafeIcon icon={FiRefreshCw} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            type="button"
            onClick={connectBank}
            disabled={!organizationId || connecting}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
          >
            <SafeIcon icon={FiPlus} />
            {connecting ? "Connecting…" : "Connect Bank Account"}
          </button>
        </div>
      </header>

      {error && (
        <div role="alert" className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          <SafeIcon icon={FiAlertTriangle} className="mt-0.5 shrink-0" />
          <div><p className="font-semibold">Banking action failed</p><p className="mt-1">{error}</p></div>
        </div>
      )}
      {notice && (
        <div role="status" className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <SafeIcon icon={FiCheckCircle} className="mt-0.5 shrink-0" />
          {notice}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <SummaryCard label="Connected accounts" value={loading ? "—" : visibleAccounts.length} detail="Active connections for this client business" icon={FiCreditCard} tone="bg-blue-50 text-blue-700" />
        <SummaryCard label="Mapped accounts" value={loading ? "—" : totals.mapped} detail="Ready for transaction synchronization" icon={FiIcons.FiCheckCircle} tone="bg-emerald-50 text-emerald-700" />
        <SummaryCard label="Not mapped" value={loading ? "—" : totals.unmapped} detail="Mapping is managed by your accounting team" icon={FiIcons.FiAlertCircle} tone="bg-amber-50 text-amber-700" />
      </div>

      <section className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">Connected bank accounts</h2>
            <p className="mt-1 text-sm text-slate-500">
              Accounts are read-only here. Ask your accounting team to update Chart of Accounts mappings.
            </p>
          </div>
          {currency && (
            <p className="text-xs text-slate-500">
              Organization currency: {currency}. Balances are not converted.
            </p>
          )}
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" aria-live="polite">
            <span className="mx-auto block h-8 w-8 animate-spin rounded-full border-2 border-emerald-100 border-t-emerald-700" />
            <p className="mt-4 text-sm text-slate-600">Loading connected accounts…</p>
          </div>
        ) : visibleAccounts.length ? (
          <div className="grid gap-4">
            {visibleAccounts.map((account) => (
              <ClientBankAccountCard
                key={account.id}
                account={account}
                currency={currency}
                syncingAccounts={busyItemId === account.plaid_item_id}
                syncingTransactions={busyAccountId === account.id}
                onSyncAccounts={syncAccounts}
                onSyncTransactions={syncTransactions}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
              <SafeIcon icon={FiCreditCard} className="text-xl" />
            </span>
            <h3 className="mt-4 font-bold text-slate-900">No connected bank accounts</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
              Connect a bank through Plaid to make its account information available to this business.
            </p>
            <button
              type="button"
              onClick={connectBank}
              disabled={!organizationId || connecting}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
            >
              <SafeIcon icon={FiPlus} />
              {connecting ? "Connecting…" : "Connect Bank Account"}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}